import React, { useEffect, useMemo, useState } from 'react';
import { format, parseISO, startOfMonth, endOfMonth } from 'date-fns';
import db from '../lib/database';
import { useStore } from '../store/StoreContext';
import { formatTime12 } from '../utils/formatters';
import { Segmented, toKeyItems, StatusPill, Button, PageHeader, Toolbar, EmptyState, RowSkeleton, pageWrapCls, tableWrapCls, tableCls, thCls, thNumCls, trCls, trClickCls, trOpenCls, tdCls, tdNumCls, tdMutedCls, trExpandCls, tdExpandCls } from '../components/ui';
import { IconClipboard } from '../components/icons';
import {
    payPeriodFor,
    shiftPayPeriod,
    formatPayPeriod,
    payPeriodIsoRange,
} from '../lib/payPeriod';

// Recaps — admin-only surface for daily-recap submissions
//
// Crew leads submit recaps from the crew portal (CrewPortalPage.jsx →
// RecapForm). Each recap lands on the move_jobs row with the actual
// start/end times, computed hours, customer satisfaction rating, crew
// notes, and damage notes. This page rolls those up by crew member for
// any date range — the headline view today is per-crew hours (the
// payroll-adjacent metric) but the drill-down per crew shows the full
// recap content.
//
// Page is named "Recaps" rather than "Hours" intentionally: hours is one
// metric within the recap, but recaps are the broader concept and the
// page will grow to include more recap content over time (a post-job
// photo, additional inspection fields, etc.).
//
// Model decision: every crew member on a job worked the same `actual_hours`
// (the recap form captures one number per job, not per-crew). So if a 6-hour
// job had 4 crew, each gets 6 hours added to their total. Person-hours total
// is hours × crew_count if we ever need that number for billing math.
//
// What's NOT in this version (deferred):
// - Pay calculation (hourly_rate column exists; multiplying is one line)
// - CSV export for payroll
// - "Pending recaps" surface (completed jobs without a recap yet)
// - Per-day breakdown within a crew member's drill-down

// ─── Date range presets ────────────────────────────────────────────────

// Compute since/until for a given preset key. Aligned to BBM's bi-weekly
// Sat–Fri pay period so recap framing matches payroll. dateRangeFor
// returns ISO strings ready for the db.getRecaps query.
function dateRangeFor(key) {
    const now = new Date();
    switch (key) {
        case 'this_period': {
            const p = payPeriodFor(now);
            const { since, until } = payPeriodIsoRange(p);
            return { since, until, label: formatPayPeriod(p) };
        }
        case 'last_period': {
            const p = shiftPayPeriod(payPeriodFor(now), -1);
            const { since, until } = payPeriodIsoRange(p);
            return { since, until, label: formatPayPeriod(p) };
        }
        case 'this_month': {
            const start = startOfMonth(now);
            const end   = endOfMonth(now);
            // Date strings, not timestamps — getRecaps/getCrewDayHours
            // filter DATE columns (move_date / work_date).
            return { since: format(start, 'yyyy-MM-dd'), until: format(end, 'yyyy-MM-dd'), label: format(now, 'MMMM yyyy') };
        }
        case 'all': {
            return { since: null, until: null, label: 'All time' };
        }
        default:
            return { since: null, until: null, label: '' };
    }
}

const PRESETS = [
    { key: 'this_period', label: 'This pay period' },
    { key: 'last_period', label: 'Last pay period' },
    { key: 'this_month',  label: 'This month'      },
    { key: 'all',         label: 'All time'        },
];

const SORT_OPTIONS = [
    { key: 'hours_desc', label: 'Hours (most first)' },
    { key: 'jobs_desc',  label: 'Jobs (most first)'  },
    { key: 'name_asc',   label: 'Name (A–Z)'         },
];

// ─── Aggregation helpers ───────────────────────────────────────────────

// Build a per-crew-member rollup from a list of recap rows. Each recap
// contributes its `actual_hours` to every crew member in `crew_ids`. Skips
// recaps with no hours recorded (recap was started but never finalized).
function aggregateByCrew(recaps, crewById, crewDayHours = []) {
    const byCrew = new Map(); // crew_id → { hours, jobs, recaps[] }

    // Daily paid hours (sql/057): a crew member's paid hours for a DATE
    // come from their crew_day_hours row, counted once. We assign that
    // daily total to the crew's primary (earliest) recap of the date and
    // 0 to the rest, so the per-recap sum below lands on the daily value.
    // Days with no row fall back to the per-job sql/052 chain.
    const dayHoursByCrewDate = new Map();
    for (const dh of (crewDayHours || [])) {
        if (!dh?.crew_member_id || !dh?.work_date) continue;
        const h = Number(dh.computed_hours);
        if (!Number.isFinite(h) || h < 0) continue;
        if (!dayHoursByCrewDate.has(dh.crew_member_id)) dayHoursByCrewDate.set(dh.crew_member_id, new Map());
        dayHoursByCrewDate.get(dh.crew_member_id).set(dh.work_date, h);
    }
    const dailyPrimaryRecap = new Map();
    {
        const byKey = new Map();
        for (const r of recaps) {
            const date = r.move_date || '';
            for (const cid of (r.crew_ids || [])) {
                if (!dayHoursByCrewDate.get(cid)?.has(date)) continue;
                const key = `${cid}|${date}`;
                if (!byKey.has(key)) byKey.set(key, []);
                byKey.get(key).push(r);
            }
        }
        for (const [key, rs] of byKey) {
            rs.sort((a, b) => (a.phase_sequence || 0) - (b.phase_sequence || 0) || String(a.id).localeCompare(String(b.id)));
            dailyPrimaryRecap.set(key, rs[0].id);
        }
    }
    const paidFor = (r, cid) => {
        const date = r.move_date || '';
        const dm = dayHoursByCrewDate.get(cid);
        if (dm && dm.has(date)) {
            return dailyPrimaryRecap.get(`${cid}|${date}`) === r.id ? dm.get(date) : 0;
        }
        // Per-job fallback (sql/052): crew clock time over billed hours.
        return (r.payroll_hours != null ? Number(r.payroll_hours) : Number(r.actual_hours)) || 0;
    };
    const dayGoverns = (r, cid) => !!dayHoursByCrewDate.get(cid)?.has(r.move_date || '');

    for (const r of recaps) {
        for (const cid of (r.crew_ids || [])) {
            const hours = paidFor(r, cid);
            if (hours <= 0 && !dayGoverns(r, cid)) continue;
            if (!byCrew.has(cid)) {
                byCrew.set(cid, { crewId: cid, hours: 0, jobs: 0, recaps: [] });
            }
            const entry = byCrew.get(cid);
            entry.hours += hours;
            entry.jobs += 1;
            entry.recaps.push(r);
        }
    }

    // Resolve crew member info from the store
    return Array.from(byCrew.values()).map(entry => {
        const member = crewById.get(entry.crewId) || {};
        return {
            ...entry,
            name: member.name || 'Unknown',
            display_color: member.display_color || '#94a3b8',
            hourly_rate: Number(member.hourly_rate) || 0,
            active: member.active !== false,
            avgHoursPerJob: entry.jobs > 0 ? entry.hours / entry.jobs : 0,
        };
    });
}

function applySort(rows, sortKey) {
    const sorted = rows.slice();
    if (sortKey === 'hours_desc') sorted.sort((a, b) => b.hours - a.hours);
    else if (sortKey === 'jobs_desc') sorted.sort((a, b) => b.jobs - a.jobs);
    else if (sortKey === 'name_asc') sorted.sort((a, b) => a.name.localeCompare(b.name));
    return sorted;
}

// ─── Crew member drill-down (chronological recap list for one person) ──

// Render the customer satisfaction rating as a row of stars. Filled stars
// = rating value, empty stars = the rest. Returns null when no rating
// was captured (recap submitted before the field was added, or lead
// just skipped it).
function StarRating({ value }) {
    if (!value || value <= 0) return <span className="text-slate-300">—</span>;
    const n = Math.max(0, Math.min(5, Math.round(value)));
    return (
        <span className="whitespace-nowrap" title={`${n} / 5`}>
            <span className="text-amber-500">{'★'.repeat(n)}</span>
            <span className="text-slate-300">{'★'.repeat(5 - n)}</span>
        </span>
    );
}

// Tender-type pill colours (cash / card / check+other) — StatusPill, lists.md §1.
const TENDER_COLOR = { cash: '#059669', card: '#3b82f6', other: '#7c3aed' };

function CrewRecapList({ row, clientsById }) {
    if (!row?.recaps?.length) return null;
    // Sort recaps within the period newest-first
    const sorted = row.recaps.slice().sort((a, b) => {
        const at = a.recap_submitted_at || a.move_date || '';
        const bt = b.recap_submitted_at || b.move_date || '';
        return bt.localeCompare(at);
    });
    return (
        <div className={tdExpandCls}>
            {/* Drill-down table — same ledger th/td classes (lists.md §3), smaller text */}
            <table className="w-full text-[12px] bg-white rounded-lg border border-slate-200">
                <thead>
                    <tr>
                        <th className={thCls}>Date</th>
                        <th className={thCls}>Client</th>
                        <th className={thCls}>Schedule</th>
                        <th className={thNumCls}>Hours</th>
                        <th className={thNumCls}>Tip</th>
                        <th className={thNumCls}>Payment</th>
                        <th className={thNumCls}>Materials</th>
                        <th className={`${thCls} text-center`}>Quality</th>
                        <th className={thCls}>Notes</th>
                    </tr>
                </thead>
                <tbody>
                    {sorted.map(r => {
                        const client = r.clients || clientsById.get(r.client_id) || {};
                        const dateLabel = r.move_date ? format(parseISO(r.move_date + 'T12:00:00'), 'EEE MMM d') : '';
                        return (
                            <tr key={r.id} className={trCls}>
                                <td className={`${tdCls} text-slate-700 whitespace-nowrap`}>{dateLabel}</td>
                                <td className={`${tdCls} text-slate-800 font-medium`}>{client.name || '—'}</td>
                                <td className={`${tdMutedCls} tabular-nums whitespace-nowrap`}>
                                    <span>
                                        {r.actual_start_time ? formatTime12(r.actual_start_time) : '—'}
                                        {' → '}
                                        {r.actual_end_time ? formatTime12(r.actual_end_time) : '—'}
                                    </span>
                                    {r.actual_lunch_minutes != null && r.actual_lunch_minutes > 0 && (
                                        <span className="ml-1.5 text-[11px] text-slate-400" title="Break (lunch + dinner/other on OOS), deducted from billed hours">· {r.actual_lunch_minutes}m break</span>
                                    )}
                                    {/* Crew clock time (sql/052) — only shown when it
                                        differs from billing time. */}
                                    {(r.payroll_start_time || r.payroll_end_time) && (
                                        <div className="text-[11px] text-violet-700" title="Crew clock time — what the crew is paid for">
                                            Clock: {r.payroll_start_time ? formatTime12(r.payroll_start_time) : '—'}
                                            {' → '}
                                            {r.payroll_end_time ? formatTime12(r.payroll_end_time) : '—'}
                                        </div>
                                    )}
                                </td>
                                <td className={`${tdNumCls} font-bold text-slate-900`}>
                                    {r.payroll_hours != null ? (
                                        <span title={`Clock hours (billed: ${Number(r.actual_hours || 0).toFixed(2)}h)`}>
                                            {Number(r.payroll_hours).toFixed(2)}h
                                            <span className="block text-[10px] font-medium text-slate-400">billed {Number(r.actual_hours || 0).toFixed(2)}h</span>
                                        </span>
                                    ) : (
                                        `${Number(r.actual_hours || 0).toFixed(2)}h`
                                    )}
                                </td>
                                <td className={`${tdNumCls} whitespace-nowrap`}>
                                    {r.tip_total != null && Number(r.tip_total) > 0 ? (
                                        <span
                                            className="inline-flex items-center gap-1"
                                            title={`${r.tip_type ? r.tip_type[0].toUpperCase() + r.tip_type.slice(1) : 'Tip'} • split across ${Object.keys(r.tip_splits || {}).length || (r.crew_ids || []).length} crew`}
                                        >
                                            <span className="font-bold text-emerald-700">${Number(r.tip_total).toFixed(2)}</span>
                                            {r.tip_type && (
                                                <StatusPill label={r.tip_type} color={TENDER_COLOR[r.tip_type] || TENDER_COLOR.other} className="font-sans" />
                                            )}
                                        </span>
                                    ) : (
                                        <span className="text-slate-300">—</span>
                                    )}
                                </td>
                                <td className={`${tdNumCls} whitespace-nowrap`}>
                                    {r.payment_amount != null && Number(r.payment_amount) > 0 ? (
                                        <span
                                            className="inline-flex items-center gap-1"
                                            title={`Paid $${Number(r.payment_amount).toFixed(2)} by ${r.payment_type || 'unspecified'}${r.payment_check_photo_url ? ' (check photo on file)' : ''}`}
                                        >
                                            <span className="font-bold text-slate-900">${Number(r.payment_amount).toFixed(2)}</span>
                                            {r.payment_type && (
                                                <StatusPill label={r.payment_type} color={TENDER_COLOR[r.payment_type] || TENDER_COLOR.other} className="font-sans" />
                                            )}
                                        </span>
                                    ) : r.type === 'PACKING' && r.packing_billing_mode === 'with_move_billing' ? (
                                        <span title="Packing fee rolls into the associated move's billing — no money collected on pack day.">
                                            <StatusPill label="→ Move" color="#d97706" className="font-sans" />
                                        </span>
                                    ) : (
                                        <span className="text-slate-300">—</span>
                                    )}
                                </td>
                                {/* Materials chip — count + revenue.
                                    Tooltip lists the top items so admin
                                    can see at a glance what was used.
                                    Click-through to JobModal still
                                    shows the full breakdown. */}
                                <td className={`${tdNumCls} whitespace-nowrap`}>
                                    {(() => {
                                        const items = Array.isArray(r.materials_used?.items) ? r.materials_used.items : [];
                                        if (items.length === 0) {
                                            return <span className="text-slate-300">—</span>;
                                        }
                                        const totalCount = items.reduce((s, it) => s + (Number(it.count) || 0), 0);
                                        const revenue = items.reduce((s, it) => s + (Number(it.count) || 0) * (Number(it.unit_price) || 0), 0);
                                        const top = items
                                            .slice()
                                            .sort((a, b) => (Number(b.count) || 0) - (Number(a.count) || 0))
                                            .slice(0, 3)
                                            .map(it => `${Number(it.count) || 0}× ${it.name}`)
                                            .join(', ');
                                        const extra = items.length > 3 ? `, +${items.length - 3} more` : '';
                                        return (
                                            <span
                                                className="inline-flex items-center gap-1 text-[11px] font-semibold"
                                                title={`${top}${extra} · Revenue $${revenue.toFixed(2)}`}
                                            >
                                                <span className="text-slate-700">📦 {totalCount}</span>
                                                {revenue > 0 && (
                                                    <span className="text-emerald-700 tabular-nums">${revenue.toFixed(2)}</span>
                                                )}
                                            </span>
                                        );
                                    })()}
                                </td>
                                {/* Quality cell: rating + photo thumbnails stacked.
                                    Combines the old separate Rating + Photos columns
                                    so the row has more headroom for Notes. */}
                                <td className={tdCls}>
                                    <div className="flex flex-col items-center gap-1">
                                        <StarRating value={r.customer_rating} />
                                        <div className="flex items-center justify-center gap-1">
                                            {r.recap_photo_url && (
                                                <a href={r.recap_photo_url} target="_blank" rel="noopener noreferrer"
                                                    onClick={e => e.stopPropagation()}
                                                    className="inline-block rounded overflow-hidden border border-slate-200 hover:border-red-300 transition-colors"
                                                    title="Reset truck photo">
                                                    <img src={r.recap_photo_url} alt="Truck" className="w-7 h-7 object-cover" loading="lazy" />
                                                </a>
                                            )}
                                            {r.bol_photo_url && (
                                                <a href={r.bol_photo_url} target="_blank" rel="noopener noreferrer"
                                                    onClick={e => e.stopPropagation()}
                                                    className="inline-block rounded overflow-hidden border border-amber-200 hover:border-amber-400 transition-colors"
                                                    title="BOL photo">
                                                    <img src={r.bol_photo_url} alt="BOL" className="w-7 h-7 object-cover" loading="lazy" />
                                                </a>
                                            )}
                                            {r.payment_check_photo_url && (
                                                <a href={r.payment_check_photo_url} target="_blank" rel="noopener noreferrer"
                                                    onClick={e => e.stopPropagation()}
                                                    className="inline-block rounded overflow-hidden border border-violet-200 hover:border-violet-400 transition-colors"
                                                    title="Check photo">
                                                    <img src={r.payment_check_photo_url} alt="Check" className="w-7 h-7 object-cover" loading="lazy" />
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </td>
                                <td className={`${tdMutedCls} max-w-xs`}>
                                    {/* Crew notes inline; damage notes get their own
                                        rose-highlighted line below since they're the
                                        kind of thing that should jump out. */}
                                    {r.crew_recap_notes && (
                                        <div className="truncate" title={r.crew_recap_notes}>{r.crew_recap_notes}</div>
                                    )}
                                    {r.damage_notes && (
                                        <div
                                            className="mt-1 inline-block px-2 py-0.5 rounded bg-rose-50 border border-rose-200 text-rose-800 text-[11px] truncate max-w-xs"
                                            title={r.damage_notes}
                                        >
                                            <span className="font-bold mr-1">Damage:</span>
                                            {r.damage_notes}
                                        </div>
                                    )}
                                    {!r.crew_recap_notes && !r.damage_notes && (
                                        <span className="text-slate-400">—</span>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

// ─── Page ──────────────────────────────────────────────────────────────

export default function RecapsPage() {
    const { crew, clients } = useStore();

    const [preset, setPreset] = useState('this_period');
    const [sortKey, setSortKey] = useState('hours_desc');
    const [recaps, setRecaps] = useState(null); // null=loading, []=empty
    const [crewDayHours, setCrewDayHours] = useState([]); // sql/057 daily paid hours
    const [error, setError] = useState(null);
    const [expandedCrewId, setExpandedCrewId] = useState(null);

    const range = useMemo(() => dateRangeFor(preset), [preset]);

    // Lookup tables for resolution. Recomputed when the store data changes.
    const crewById = useMemo(() => {
        const m = new Map();
        (crew || []).forEach(c => m.set(c.id, c));
        return m;
    }, [crew]);
    const clientsById = useMemo(() => {
        const m = new Map();
        (clients || []).forEach(c => m.set(c.id, c));
        return m;
    }, [clients]);

    // Fetch on mount + whenever the date range changes.
    useEffect(() => {
        let cancelled = false;
        setRecaps(null);
        setError(null);
        db.getRecaps({ since: range.since || undefined, until: range.until || undefined })
            .then(rows => { if (!cancelled) setRecaps(rows); })
            .catch(e => { if (!cancelled) setError(e?.message || 'Failed to load recaps'); });
        // Daily paid hours (sql/057) — non-fatal on pre-057 deploys.
        db.getCrewDayHours({ since: range.since || undefined, until: range.until || undefined })
            .then(dh => { if (!cancelled) setCrewDayHours(Array.isArray(dh) ? dh : []); })
            .catch(() => { if (!cancelled) setCrewDayHours([]); });
        return () => { cancelled = true; };
    }, [range.since, range.until]);

    const rows = useMemo(() => {
        if (!Array.isArray(recaps)) return [];
        return applySort(aggregateByCrew(recaps, crewById, crewDayHours), sortKey);
    }, [recaps, crewById, crewDayHours, sortKey]);

    const totalHours = useMemo(() => rows.reduce((acc, r) => acc + r.hours, 0), [rows]);
    const totalJobs  = useMemo(() => Array.isArray(recaps) ? recaps.length : 0, [recaps]);

    const summary = recaps === null
        ? range.label
        : `${rows.length} crew · ${totalHours.toFixed(2)} hrs · ${totalJobs} ${totalJobs === 1 ? 'recap' : 'recaps'} · ${range.label}`;

    return (
        <div className={pageWrapCls}>
            <PageHeader
                title="Recaps"
                summary={summary}
                help={<>
                    <p>Daily recap submissions from crew leads — hours, customer satisfaction, crew notes, damage reports. Click a crew member to see their individual recaps for the period.</p>
                    <p className="mt-2">Hours here are what the recaps report happened on the job. Payroll applies per-person pay adjustments (overrides, day-clock rows) on top — so the Payroll page's paid hours can differ by design.</p>
                    <p className="mt-2">Each crew member on a job is credited the job's actual hours. Times shown in Eastern Time (EST). Pay calculation lives on the Payroll page.</p>
                </>}
            />

            {/* Toolbar — preset segmented + sort */}
            <Toolbar>
                <Segmented
                    size="sm"
                    ariaLabel="Date range"
                    items={toKeyItems(PRESETS)}
                    value={preset}
                    onChange={v => setPreset(v)}
                />
                <span className="text-xs text-slate-400 tabular-nums">{range.label}</span>

                <Segmented
                    size="sm"
                    ariaLabel="Sort by"
                    items={toKeyItems(SORT_OPTIONS)}
                    value={sortKey}
                    onChange={v => setSortKey(v)}
                    className="ml-auto"
                />
            </Toolbar>

            {/* Summary tiles */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Total hours</div>
                    <div className="text-2xl font-bold text-slate-900 tabular-nums">{totalHours.toFixed(2)}</div>
                    <div className="text-[11px] text-slate-400">across all crew</div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Recaps submitted</div>
                    <div className="text-2xl font-bold text-slate-900 tabular-nums">{totalJobs}</div>
                    <div className="text-[11px] text-slate-400">jobs in range</div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Active crew</div>
                    <div className="text-2xl font-bold text-slate-900 tabular-nums">{rows.length}</div>
                    <div className="text-[11px] text-slate-400">with logged hours</div>
                </div>
            </div>

            {/* Table — ledger recipe, see BBM Context for claude/lists.md §3 */}
            <div className={tableWrapCls}>
                {recaps === null && <RowSkeleton rows={5} />}
                {error && (
                    <div className="px-4 py-3 text-sm font-semibold text-red-600 bg-red-50 border-b border-red-200">{error}</div>
                )}
                {Array.isArray(recaps) && rows.length === 0 && !error && (
                    <EmptyState
                        icon={<IconClipboard size={28} />}
                        title={preset === 'all' ? 'No recaps submitted yet' : 'No recaps in this range'}
                        hint={preset === 'all'
                            ? 'Recaps land here once crew leads submit them from the crew portal.'
                            : 'Pick a wider window or check that crews are submitting recaps from the crew portal.'}
                        action={preset !== 'all'
                            ? <Button variant="tertiary" size="sm" onClick={() => setPreset('all')}>Show all time</Button>
                            : <Button variant="secondary" size="sm" onClick={() => { window.location.hash = 'moves'; }}>Go to Active Moves</Button>}
                        className="border-0 rounded-none"
                    />
                )}
                {rows.length > 0 && (
                    <table className={tableCls}>
                        <thead>
                            <tr>
                                <th className={thCls}>Crew member</th>
                                <th className={`${thNumCls} w-28`}>Hours</th>
                                <th className={`${thNumCls} w-24`}>Jobs</th>
                                <th className={`${thNumCls} w-32`}>Avg / job</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(row => {
                                const isExpanded = expandedCrewId === row.crewId;
                                return (
                                    <React.Fragment key={row.crewId}>
                                        <tr
                                            onClick={() => setExpandedCrewId(isExpanded ? null : row.crewId)}
                                            className={`${trClickCls} ${isExpanded ? trOpenCls : ''}`}
                                        >
                                            <td className={tdCls}>
                                                <div className="flex items-center gap-2">
                                                    <span
                                                        className="text-xs font-bold px-2 py-0.5 rounded-md text-white"
                                                        style={{ background: row.display_color }}
                                                    >
                                                        {row.name}
                                                    </span>
                                                    {!row.active && (
                                                        <span className="text-[10px] text-slate-400 italic">inactive</span>
                                                    )}
                                                    <span aria-hidden="true" className="text-slate-300 text-xs ml-1">
                                                        {isExpanded ? '▾' : '▸'}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className={`${tdNumCls} font-bold text-slate-900`}>{row.hours.toFixed(2)}</td>
                                            <td className={`${tdNumCls} text-slate-700`}>{row.jobs}</td>
                                            <td className={`${tdNumCls} text-slate-500`}>{row.avgHoursPerJob.toFixed(2)}</td>
                                        </tr>
                                        {isExpanded && (
                                            <tr className={trExpandCls}>
                                                <td colSpan={4} className="p-0">
                                                    <CrewRecapList row={row} clientsById={clientsById} />
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
